/**
 * One facility as a sales account: what the TM board says, the building's
 * contacts, eMerge's sales state (owner, stage, interest per service line,
 * competitor, next step), a quick way to log a touch with its disposition,
 * and the history.
 *
 * Contacts (authority ruling 2026-10-05: facility contacts are EMERGE_OWNED and
 * kept in one place) are the facility directory, the same section the
 * Scheduling facility panel shows, editable here by whoever holds
 * facility.contact.write and read-only otherwise. The TM board's free-text
 * "Faciliy Contacts" notes that earlier imports left on the account are
 * HISTORY_ONLY: shown under that name, never edited.
 *
 * Opened from Today, Accounts and the Map, so a rep never has to leave the
 * screen they are on to record what just happened.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CalendarPlus, ClipboardCheck, History, Save } from 'lucide-react';

import { Button } from '../../../components/new-components/ui/button';
import { Textarea } from '../../../components/new-components/ui/textarea';
import { Input } from '../../../components/new-components/ui/input';
import {
  Account, Activity, ActivityKind, Interest, Lead, Meta, Sale, ServiceLine, Stage,
  bdApi, fmtDate, fmtMoney, SERVICE_LABEL, STAGE_LABEL, words,
} from './bdApi';
import { Drawer, NativeSelect, StagePill, StateLine } from './bdUi';
import FacilityContactsSection from '../../../components/facilityContacts/FacilityContactsSection';
import type { ContactDirectory } from '../../../components/facilityContacts/facilityContactsApi';

type Detail = {
  account: Account; activities: Activity[]; sales: Sale[]; leads: Lead[]; visits: { id: string; title: string; starts_at: string; status: string; host_name: string | null }[];
  directory?: ContactDirectory | null; directory_error?: boolean;
};

const INTEREST_KEY: Record<ServiceLine, keyof Account> = {
  PODIATRY: 'podiatry_interest', AUDIOLOGY: 'audiology_interest', OPTOMETRY: 'optometry_interest', DENTISTRY: 'dentistry_interest',
};

export default function AccountPanel({
  facilityId, meta, onClose, onChanged, calendarEventId,
}: {
  facilityId: string | null;
  meta: Meta;
  onClose: () => void;
  onChanged?: () => void;
  /** When opened from a calendar visit, a logged visit completes that entry. */
  calendarEventId?: string | null;
}) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true); setError(null);
    const r = await bdApi.account(facilityId);
    setLoading(false);
    if (r.success) setDetail(r.data); else setError(r.error || 'This account could not be loaded.');
  }, [facilityId]);

  useEffect(() => { setDetail(null); load(); }, [load]);

  const changed = async () => { await load(); onChanged?.(); };

  return (
    <Drawer
      open={Boolean(facilityId)}
      onClose={onClose}
      title={detail?.account.name || 'Account'}
      description={detail ? [detail.account.facility_type, detail.account.city, detail.account.region].filter(Boolean).join(' · ') : undefined}
    >
        <div className="px-4 pt-4 pb-8 space-y-6">
          <StateLine loading={loading && !detail} error={error} onRetry={load} />
          {detail && (
            <>
              <BoardFacts a={detail.account} />
              <FacilityContactsSection facilityId={detail.account.facility_id} facilityName={detail.account.name}
                fallback={detail.directory || null} fallbackError={Boolean(detail.directory_error)} />
              <LogTouch facilityId={detail.account.facility_id} meta={meta} calendarEventId={calendarEventId} onDone={changed} />
              <SalesState a={detail.account} meta={meta} onSaved={changed} />
              <PlanVisit facilityId={detail.account.facility_id} onDone={changed} />
              <HistoryList d={detail} />
            </>
          )}
        </div>
    </Drawer>
  );
}

function BoardFacts({ a }: { a: Account }) {
  const rows: [string, React.ReactNode][] = [
    ['Stage', <StagePill key="s" stage={a.stage} board={a.stage_source === 'EMERGE' ? a.board_stage : null} />],
    ['Owner', a.owner_name || <span className="text-muted-foreground">Unassigned</span>],
    ['Address', [a.address, a.city].filter(Boolean).join(', ') || '—'],
    ['Phone / email', [a.phone, a.email].filter(Boolean).join(' · ') || '—'],
    ['Corporation', a.corporation?.join(', ') || '—'],
    ['Beds', a.beds ?? a.licensed_beds ?? '—'],
    ['BAA', a.baa_signed ? `Signed ${fmtDate(a.baa_signed_date)}` : a.baa_sent ? `Sent ${fmtDate(a.baa_sent_date)}` : 'Not sent'],
    ['Credentialing', a.credentialing_status || '—'],
    ['Provider visits', `Last ${fmtDate(a.last_visit_date)} · next ${fmtDate(a.provider_next_visit_date)}`],
    ['Current provider', a.current_provider || '—'],
  ];
  return (
    <section aria-label="What the board says">
      <dl className="grid grid-cols-[8.5rem_1fr] gap-x-3 gap-y-1.5 text-sm">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="min-w-0 break-words">{v}</dd>
          </React.Fragment>
        ))}
      </dl>
      {a.key_contacts && (
        <div className="mt-3 rounded-md bg-muted/50 p-3 text-sm whitespace-pre-wrap" role="note" aria-label="TM board contact notes (history only)">
          <span className="text-xs text-muted-foreground block mb-1">TM board contact notes (history only, not kept up to date). The building’s contacts are in Contacts.</span>
          {a.key_contacts}
        </div>
      )}
    </section>
  );
}

function LogTouch({ facilityId, meta, calendarEventId, onDone }: { facilityId: string; meta: Meta; calendarEventId?: string | null; onDone: () => void }) {
  const [kind, setKind] = useState<ActivityKind>('VISIT');
  const [disposition, setDisposition] = useState('');
  const [lines, setLines] = useState<ServiceLine[]>([]);
  const [note, setNote] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [nextOn, setNextOn] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const implied = disposition ? meta.stage_from_disposition[disposition] : undefined;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    const r = await bdApi.logActivity(facilityId, {
      kind, disposition: disposition || null, service_lines: lines, note,
      calendar_event_id: calendarEventId || undefined, next_step: nextStep || undefined, next_step_on: nextOn || undefined,
    });
    setBusy(false);
    if (!r.success) { setMsg({ ok: false, text: r.error || 'Not saved.' }); return; }
    setMsg({ ok: true, text: r.data.stage_moved ? `Logged. Stage is now ${STAGE_LABEL[r.data.stage]}.` : 'Logged.' });
    setDisposition(''); setLines([]); setNote(''); setNextStep(''); setNextOn('');
    onDone();
  };

  return (
    <section aria-labelledby="log-touch" className="rounded-lg border p-4 space-y-3">
      <h3 id="log-touch" className="text-sm font-semibold flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> Log a touch</h3>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <NativeSelect label="What" value={kind} onChange={(v) => setKind(v as ActivityKind)}>
            {meta.activity_kinds.map((k) => <option key={k} value={k}>{words(k)}</option>)}
          </NativeSelect>
          <NativeSelect label="Disposition" value={disposition} onChange={setDisposition}>
            <option value="">— none —</option>
            {meta.dispositions.map((d) => <option key={d} value={d}>{words(d)}</option>)}
          </NativeSelect>
        </div>
        <fieldset>
          <legend className="text-xs text-muted-foreground mb-1">Service lines discussed</legend>
          <div className="flex flex-wrap gap-2">
            {meta.service_lines.map((s) => {
              const on = lines.includes(s);
              return (
                <button
                  key={s} type="button" aria-pressed={on}
                  onClick={() => setLines(on ? lines.filter((x) => x !== s) : [...lines, s])}
                  className={`rounded-full border px-3 py-1 text-xs ${on ? 'bg-foreground text-background border-foreground' : 'bg-background'}`}
                >{SERVICE_LABEL[s]}</button>
              );
            })}
          </div>
        </fieldset>
        <Textarea aria-label="Note" placeholder="What happened, who you met, what they need" value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
        <div className="grid grid-cols-[1fr_10rem] gap-3">
          <Input aria-label="Next step" placeholder="Next step (optional)" value={nextStep} onChange={(e) => setNextStep(e.target.value)} />
          <Input aria-label="Next step date" type="date" value={nextOn} onChange={(e) => setNextOn(e.target.value)} />
        </div>
        {implied && <p className="text-xs text-muted-foreground">This disposition moves the stage to <b>{STAGE_LABEL[implied]}</b>.</p>}
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={busy || (kind === 'NOTE' && !note.trim() && !disposition)}>{busy ? 'Saving…' : 'Log it'}</Button>
          {msg && <span role={msg.ok ? 'status' : 'alert'} className={`text-sm ${msg.ok ? 'text-emerald-700' : 'text-destructive'}`}>{msg.text}</span>}
        </div>
      </form>
    </section>
  );
}

function SalesState({ a, meta, onSaved }: { a: Account; meta: Meta; onSaved: () => void }) {
  const [draft, setDraft] = useState(() => ({
    owner_user_id: a.owner_user_id || '',
    stage: a.stage as Stage,
    current_provider: a.current_provider || '',
    next_step: a.next_step || '',
    next_step_on: a.next_step_on ? String(a.next_step_on).slice(0, 10) : '',
    ...Object.fromEntries(meta.service_lines.map((s) => [INTEREST_KEY[s], (a[INTEREST_KEY[s]] as Interest | null) || ''])),
  } as Record<string, string>));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const set = (k: string) => (v: string) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    const patch: Record<string, unknown> = {
      owner_user_id: draft.owner_user_id || null,
      current_provider: draft.current_provider || null,
      next_step: draft.next_step || null,
      next_step_on: draft.next_step_on || null,
    };
    if (draft.stage !== a.stage || a.stage_source === 'EMERGE') patch.stage = draft.stage;
    for (const s of meta.service_lines) patch[INTEREST_KEY[s]] = draft[INTEREST_KEY[s]] || null;
    const r = await bdApi.updateAccount(a.facility_id, patch as Partial<Account>);
    setBusy(false);
    if (!r.success) { setMsg({ ok: false, text: r.error || 'Not saved.' }); return; }
    setMsg({ ok: true, text: 'Saved.' });
    onSaved();
  };

  return (
    <section aria-labelledby="sales-state" className="rounded-lg border p-4 space-y-3">
      <h3 id="sales-state" className="text-sm font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Account</h3>
      <form onSubmit={save} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <NativeSelect label="Owner" value={draft.owner_user_id} onChange={set('owner_user_id')}>
            <option value="">Unassigned</option>
            {meta.reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </NativeSelect>
          <NativeSelect label="Stage" value={draft.stage} onChange={set('stage')}>
            {meta.stages.map((s) => <option key={s.key} value={s.key}>{STAGE_LABEL[s.key]}</option>)}
          </NativeSelect>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {meta.service_lines.map((s) => (
            <NativeSelect key={s} label={SERVICE_LABEL[s]} value={draft[INTEREST_KEY[s]]} onChange={set(INTEREST_KEY[s])}>
              <option value="">Not asked</option>
              {meta.interests.map((i) => <option key={i} value={i}>{words(i)}</option>)}
            </NativeSelect>
          ))}
        </div>
        <Input aria-label="Current provider (competitor)" placeholder="Current provider (competitor)" value={draft.current_provider} onChange={(e) => set('current_provider')(e.target.value)} />
        <div className="grid grid-cols-[1fr_10rem] gap-3">
          <Input aria-label="Next step" placeholder="Next step" value={draft.next_step} onChange={(e) => set('next_step')(e.target.value)} />
          <Input aria-label="Next step date" type="date" value={draft.next_step_on} onChange={(e) => set('next_step_on')(e.target.value)} />
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" variant="outline" disabled={busy}>{busy ? 'Saving…' : 'Save account'}</Button>
          {msg && <span role={msg.ok ? 'status' : 'alert'} className={`text-sm ${msg.ok ? 'text-emerald-700' : 'text-destructive'}`}>{msg.text}</span>}
        </div>
      </form>
    </section>
  );
}

function PlanVisit({ facilityId, onDone }: { facilityId: string; onDone: () => void }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [mins, setMins] = useState('60');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const plan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) return;
    // The rep picks a New York wall-clock time, whatever zone the laptop is in.
    const starts = nyWallClockToDate(date, time);
    const ends = new Date(starts.getTime() + Number(mins) * 60000);
    setBusy(true); setMsg(null);
    const r = await bdApi.planVisit({ facility_id: facilityId, starts_at: starts.toISOString(), ends_at: ends.toISOString() });
    setBusy(false);
    if (!r.success) { setMsg({ ok: false, text: r.error || 'Not added.' }); return; }
    setMsg({ ok: true, text: 'Added to your calendar.' });
    setDate('');
    onDone();
  };

  return (
    <section aria-labelledby="plan-visit" className="rounded-lg border p-4 space-y-3">
      <h3 id="plan-visit" className="text-sm font-semibold flex items-center gap-2"><CalendarPlus className="w-4 h-4" /> Plan a visit</h3>
      <form onSubmit={plan} className="flex flex-wrap items-end gap-3">
        <Input aria-label="Visit date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />
        <Input aria-label="Visit time (Eastern)" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-32" />
        <NativeSelect label="Length" hideLabel value={mins} onChange={setMins}>
          {['30', '45', '60', '90', '120'].map((m) => <option key={m} value={m}>{m} min</option>)}
        </NativeSelect>
        <Button type="submit" size="sm" variant="outline" disabled={busy || !date}>{busy ? 'Adding…' : 'Add to calendar'}</Button>
        {msg && <span role={msg.ok ? 'status' : 'alert'} className={`text-sm ${msg.ok ? 'text-emerald-700' : 'text-destructive'}`}>{msg.text}</span>}
      </form>
    </section>
  );
}

/**
 * "2026-10-01" + "10:00" in New York -> the instant. New York's offset on that
 * day ("GMT-4" in summer, "GMT-5" in winter) comes from the browser's own tz
 * data, read at noon so a DST switch at 2am cannot shift the answer.
 */
export function nyWallClockToDate(date: string, time: string): Date {
  const noon = new Date(`${date}T12:00:00Z`);
  const part = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', timeZoneName: 'shortOffset' })
    .formatToParts(noon).find((p) => p.type === 'timeZoneName')?.value || 'GMT-5';
  const m = part.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  const sign = m ? m[1] : '-';
  const hh = String(m ? m[2] : '5').padStart(2, '0');
  const mm = m && m[3] ? m[3] : '00';
  return new Date(`${date}T${time}:00${sign}${hh}:${mm}`);
}

function HistoryList({ d }: { d: Detail }) {
  return (
    <section aria-labelledby="history" className="space-y-3">
      <h3 id="history" className="text-sm font-semibold flex items-center gap-2"><History className="w-4 h-4" /> History</h3>
      {d.sales.length > 0 && (
        <ul className="space-y-1 text-sm">
          {d.sales.map((s) => (
            <li key={s.id} className={s.status === 'REVERSED' ? 'line-through text-muted-foreground' : ''}>
              <b>Won</b> {s.service_lines.map((l) => SERVICE_LABEL[l]).join(', ')} · {fmtDate(s.signed_on)} · {s.user_name || '—'}
              {s.monthly_value_cents != null && ` · ${fmtMoney(s.monthly_value_cents)}/mo`}
            </li>
          ))}
        </ul>
      )}
      {d.activities.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing logged yet.</p>
      ) : (
        <ol className="space-y-2">
          {d.activities.map((a) => (
            <li key={a.id} className="border-l-2 pl-3 text-sm">
              <div className="text-xs text-muted-foreground">
                {new Date(a.occurred_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' })} · {a.user_name || '—'}
              </div>
              <div>
                <b>{words(a.kind)}</b>
                {a.disposition && <> · {words(a.disposition)}</>}
                {a.stage_to && <> · {STAGE_LABEL[a.stage_from as Stage] || '—'} → {STAGE_LABEL[a.stage_to]}</>}
                {a.service_lines?.length > 0 && <> · {a.service_lines.map((l) => SERVICE_LABEL[l]).join(', ')}</>}
              </div>
              {a.note && <div className="whitespace-pre-wrap text-muted-foreground">{a.note}</div>}
            </li>
          ))}
        </ol>
      )}
      {d.leads.length > 0 && (
        <div className="text-sm">
          <div className="text-xs text-muted-foreground mb-1">Contacts at this facility</div>
          <ul className="space-y-0.5">
            {d.leads.map((l) => <li key={l.id}>{l.first_name} {l.last_name}{l.title ? `, ${l.title}` : ''}{l.email ? ` · ${l.email}` : ''}</li>)}
          </ul>
        </div>
      )}
    </section>
  );
}
